import { differenceInSeconds } from 'date-fns';
import { round } from 'lodash';
import { clamp } from '../../../CodesThatsGottaGoSomewhereElse';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { component, ComponentTuple, ObjectManager, property } from '../../../concepts/objects';
import { DateType } from '../../../concepts/objects/DateType';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import { FixedRectangular } from '../../canvas/entities/FixedRectangular';
import { Figure, Physical, PotentialInteraction, RelativeTo, TransformHints, TransformScope } from '../../canvas/entities/Physical';
import { Textable } from '../../canvas/entities/Textable';
import { GraffitiTarget } from '../../canvas/visualization/Graffiti';
import { Commentable } from '../annotations/Commentable';
import { CommentCountAttr, CommentOpenAttr } from '../annotations/CommentAssociations';
import { commentsOpen } from '../annotations/CommentCommands';
import { Tag } from '../annotations/Tag';
import { tagsToggleEdit } from '../annotations/TaggingCommands';
import { TagSupport } from '../annotations/TagSupport';
import { TaskRecordAttr } from '../assignment/TaskAssocations';
import { Audited } from '../auditing/Audited';
import { AutoLayoutSubject } from '../autoLayout/AutoLayout';
import { ConnectTarget } from '../connector/ConnectTarget';
import { Pingable } from '../presentation/Pingable';
import { Votable } from '../voting/Votable';
import { VoteCountAttr } from '../voting/Vote';
import { votesOpen } from '../voting/VotingCommands';
import { TaskCardContentField } from './TaskCardContentField';
import { TaskCardLayout } from './TaskCardLayout';
import { taskCardPropertyLayout } from './TaskCardPropertyLayout';
import { TaskCardTitleField } from './TaskCardTitleField';

const mutation = defineCommand(
    'taskCardMutate',
    'TaskCard Mutate',
    (r, [id]: [string]) => {
        const tc = r.call(ObjectManager).get(id, TaskCard);
        const isNew = differenceInSeconds(new Date(), tc.created) < 1;
        // If the card was created less than a second ago it's new.  If so, default to the title field.  This is basically a hack to prevent
        // the "plop" from adding a new index card selecting the body instead of the title.
        const field = (
            (isNew ? 'title' : null) ??
            (!tc.title ? 'title' : null) ??
            '' // Auto
        );
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field', field));
    },
    [],
);

const invocation = defineCommand(
    'taskCardInvoke',
    null,
    (r, [id, hint]: [string, string?]) => {
        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);

        if (hint === 'comments') {
            dispatch(commentsOpen, [id]);
        }
        else if (hint === 'votes') {
            dispatch(votesOpen, [id]);
        }
        // else if (hint === 'flip' && IsConcealedHintVisible.get(objects.get(id, IndexCard))) {
        //     dispatch(flip, [[id]]);
        // }
        else {
            const tag = objects.getMaybe(hint, Tag);
            if (!!tag) dispatch(tagsToggleEdit, [tag.id, true]);
        }
    },
    [],
);

const taskCardDef = {
    name: 'TaskCard',
    implements: [
        Physical,
        AutoBringToFront,
        AutoLayoutSubject,
        Audited,
        ConnectTarget,
        Pingable,
        GraffitiTarget,
        Commentable,
        Votable,
        TagSupport,
        Textable
    ],
    mutation,
    invocation,
    propertyLayout: taskCardPropertyLayout,
};

@component(taskCardDef)
export class TaskCard extends FixedRectangular implements AutoBringToFront, Audited, Pingable, Commentable, Votable, TagSupport, Textable {

    @property({ type: 'number', default: 300 })
    public width!: number;

    @property({ type: 'number', default: 200 })
    public height!: number;

    @property({ type: 'string', default: '#2196f3' })
    public color!: string;

    @property({ type: 'string' })
    public autoColor!: string;

    @property({ type: 'string', oldName: 'title|value' })
    public title!: string;

    @property({ type: 'string', oldName: 'content|value' })
    public content!: string;

    @property({ type: 'string' })
    public userId!: string;

    @property({ type: DateType })
    public created!: Date;

    public get boundary() {
        return true;
    }

    public get minWidth() {
        return 160;
    }
    public get minHeight() {
        return 80;
    }

    @memoUsing(
        'children', 'width', 'height', 'title', 'content',
        CommentCountAttr, CommentOpenAttr, TaskRecordAttr, VoteCountAttr,
    )
    public get layout() {
        return TaskCardLayout(this);
    }

    @memoUsing('layout')
    public get uiFields() {
        return [
            TaskCardTitleField(this),
            TaskCardContentField(this),
        ];
    }

    public queryTransformHints(singular: boolean): TransformHints {
        return TransformHints({
            scaleX: true,
            scaleY: true,
            rotate: false,
            translate: true,
            visible: !singular,
        });
    }

    public testInteraction({ type, mpt }: PotentialInteraction, _force?: boolean, _selected?: boolean): [boolean, string] {
        const lpt = this.toRelative(mpt);
        if (type === 'invoke') {
            for (const [id, area] of Object.entries(this.layout.tags)) {
                if (area.contains(lpt)) return [true, id];
            }
        }
        // Any non invokes on the whole frame
        if (this.localBounds.contains(lpt)) return [type !== 'invoke', 'default'];
        return [false, 'none'];
    }

    // protected setTransformationResult(x: number, y: number, r: number, w: number, h: number): void {
    //     const newBounds = new Rect(x, y, w, h);
    //     const newCenter = newBounds.center();

    //     w = this.layout.width;
    //     h = this.layout.height;
    //     x = newCenter.x - (w / 2);
    //     y = newCenter.y - (h / 2);
    //     r = 0;

    //     super.setTransformationResult(x, y, r, w, h);
    // }

    public applyTransformation(scope: TransformScope, from: Figure, to: Figure): this {
        // Figures are always model level, so we need to apply the inverse transform of the parent to localize it.
        const tlt = this.modelLocalTransform();
        from = tlt.apply(from);
        to = tlt.apply(to);

        const [a, b, c] = scope.type === 'scale' ? this.jiggleForPotentialMirror(from, to) : to;

        // prev
        const px = from[0].x;
        const py = from[0].y;
        const pw = Point.vector(from[0], from[1]).length();
        const ph = Point.vector(from[1], from[2]).length();

        // width & height vectors
        const wv = Point.vector(a, b);
        const hv = Point.vector(b, c);

        // Calcualte new center point
        const nc = Rect.fromPoints(to).center();

        // Making use of the TransformScope information, if we can tell that the transform is a scale only upon a single Sticky, we should
        // prevent the sticky from moving so the resize action doesn't feel too strange.  To do this, restrict to cases where x movement
        // is within a single resize range and the width actually changes.
        if (scope.type === 'scale' && scope.items.length === 1) {

            const x = px + clamp(a.x - px, 20);
            const y = py + clamp(b.y - py, 20);
            const w = Math.max(pw + clamp(wv.length() - pw, 20), this.minWidth);
            const h = Math.max(ph + clamp(hv.length() - ph, 20), this.minHeight);

            if (this.width !== w || this.height !== h) {
                this.setTransformationResult(
                    round(x, 4),
                    round(y, 4),
                    0,
                    round(w, 4),
                    round(h, 4),
                );
            }
        }
        else {
            this.setTransformationResult(
                round(nc.x - (pw / 2), 4),
                round(nc.y - (ph / 2), 4),
                round(0, 4),
                round(pw, 4),
                round(this.layout.height, 4),
            );
        }

        return this;
    }

    public toEmptyTuple(to?: Physical | RelativeTo): ComponentTuple {
        const ct = super.toShallowTuple(to);
        ct[2].title = '';
        ct[2].content = '';
        return ct;
    }

    public toText(): string {
        return [this.title, this.content].filter(x => !!x).join(':\n');
    }

    public toTextArray(): string[] {
        return [this.title, this.content];
    }

    public toHumanString(): string {
        return `TaskCard ("${this.title.slice(0, 20)}")`;
    }
}
